/**
 * Локальный IPv4 для журнала аудита: при Docker NAT сервер видит не LAN машины.
 * Берём hostname, env, либо (для localhost) — host-кандидаты WebRTC.
 */

const STORAGE_KEY = 'lv_audit_client_visible_ipv4'

let rtcDiscovery: Promise<string | null> | null = null

function isPrivateIPv4(s: string): boolean {
  const p = s.split('.').map(Number)
  if (p.length !== 4 || p.some((n) => Number.isNaN(n) || n > 255))
    return false
  const [a, b] = p
  if (a === 10)
    return true
  if (a === 172 && b >= 16 && b <= 31)
    return true
  if (a === 192 && b === 168)
    return true
  return false
}

function pickBestIPv4(candidates: string[]): string | null {
  const priv = candidates.filter(
    ip => isPrivateIPv4(ip) && !ip.startsWith('127.'),
  )
  if (priv.length === 0)
    return null
  const pref = priv.find(ip => ip.startsWith('192.168.'))
  return pref ?? priv[0]
}

function extractHostIPv4FromCandidate(candidate: string): string | null {
  if (!candidate.includes('typ host'))
    return null
  const re = /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/g
  let m: RegExpExecArray | null
  while ((m = re.exec(candidate)) !== null) {
    const ip = m[1]
    if (isPrivateIPv4(ip) && !ip.startsWith('127.'))
      return ip
  }
  return null
}

/** Chrome часто кладёт host-кандидаты в localDescription.sdp. Берём любые приватные IPv4 из строк candidate (не только typ host — иначе mDNS/упрощённый SDP дают best=null). */
function extractPrivateIPv4FromSDP(sdp: string): string | null {
  const acc = new Set<string>()
  for (const line of sdp.split(/\r?\n/)) {
    if (!line.toLowerCase().includes('candidate'))
      continue
    const re = /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/g
    let m: RegExpExecArray | null
    while ((m = re.exec(line)) !== null) {
      const ip = m[1]
      if (isPrivateIPv4(ip) && !ip.startsWith('127.'))
        acc.add(ip)
    }
  }
  return pickBestIPv4([...acc])
}

function discoverLanIPv4ViaWebRTC(): Promise<string | null> {
  if (typeof RTCPeerConnection === 'undefined')
    return Promise.resolve(null)

  const found = new Set<string>()
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  })
  pc.createDataChannel('')

  const mergeBest = (): string | null => {
    const fromEvents = pickBestIPv4([...found])
    const fromSdp = extractPrivateIPv4FromSDP(pc.localDescription?.sdp ?? '')
    if (fromEvents && fromSdp)
      return fromEvents.startsWith('192.168.') ? fromEvents : (fromSdp.startsWith('192.168.') ? fromSdp : fromEvents)
    return fromEvents ?? fromSdp
  }

  const finish = (resolve: (v: string | null) => void) => {
    const best = mergeBest()
    pc.close()
    resolve(best)
  }

  return new Promise((resolve) => {
    const t = setTimeout(() => finish(resolve), 2500)

    pc.onicecandidate = (ev) => {
      const cand = ev.candidate?.candidate
      if (cand) {
        const ip = extractHostIPv4FromCandidate(cand)
        if (ip)
          found.add(ip)
      }
      else {
        clearTimeout(t)
        finish(resolve)
      }
    }

    pc.createOffer()
      .then(offer => pc.setLocalDescription(offer))
      .catch(() => {
        clearTimeout(t)
        pc.close()
        resolve(null)
      })
  })
}

/**
 * IPv4, который имеет смысл записывать как «клиент» в аудите (синхронно, без WebRTC).
 */
export function getClientVisibleIPv4Sync(): string | null {
  const fromEnv = String(import.meta.env.VITE_CLIENT_VISIBLE_IP ?? '').trim()
  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(fromEnv))
    return fromEnv

  if (typeof window === 'undefined')
    return null

  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1')
    return null

  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(host))
    return host

  return null
}

/**
 * Полный резолв с кэшем и WebRTC для localhost.
 */
export async function getClientVisibleIPv4ForAudit(): Promise<string | null> {
  if (typeof window === 'undefined')
    return null

  const cached = sessionStorage.getItem(STORAGE_KEY)
  if (cached && /^(?:\d{1,3}\.){3}\d{1,3}$/.test(cached))
    return cached

  const sync = getClientVisibleIPv4Sync()
  if (sync) {
    sessionStorage.setItem(STORAGE_KEY, sync)
    return sync
  }

  const host = window.location.hostname
  if (host !== 'localhost' && host !== '127.0.0.1')
    return null

  if (rtcDiscovery) {
    const v = await rtcDiscovery
    return v
  }

  rtcDiscovery = discoverLanIPv4ViaWebRTC()
    .then((ip) => {
      if (ip)
        sessionStorage.setItem(STORAGE_KEY, ip)
      return ip
    })
    .catch(() => null)

  return await rtcDiscovery
}
